/**
 * formEntryStore.ts
 * Self-hosted form entry storage using localStorage.
 * No third-party services required. Entries persist across sessions.
 * When you move to your own hosting, swap this out for a real API call.
 */

export interface StoredFormEntry {
  id: string;
  formId: string;
  formLabel: string;
  submittedAt: string; // ISO string
  fields: { label: string; value: string }[];
}

const STORAGE_KEY = "ma_form_entries_v1";

function loadEntries(): StoredFormEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as StoredFormEntry[];
  } catch { /* ignore */ }
  return [];
}

function saveEntries(entries: StoredFormEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch { /* ignore */ }
}

export function storeFormEntry(
  formId: string,
  formLabel: string,
  fields: { label: string; value: string }[]
): StoredFormEntry {
  const entry: StoredFormEntry = {
    id: `${formId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    formId,
    formLabel,
    submittedAt: new Date().toISOString(),
    fields: fields.filter((f) => f.value.trim() !== ""),
  };
  const existing = loadEntries();
  // Keep max 500 entries to avoid localStorage bloat
  const updated = [entry, ...existing].slice(0, 500);
  saveEntries(updated);
  return entry;
}

export function getFormEntries(formId?: string): StoredFormEntry[] {
  const all = loadEntries();
  if (!formId) return all;
  return all.filter((e) => e.formId === formId);
}

export function getAllFormEntries(): StoredFormEntry[] {
  return loadEntries();
}

export function deleteFormEntry(id: string): void {
  const entries = loadEntries().filter((e) => e.id !== id);
  saveEntries(entries);
}

export function clearFormEntries(formId?: string): void {
  if (!formId) {
    saveEntries([]);
    return;
  }
  const entries = loadEntries().filter((e) => e.formId !== formId);
  saveEntries(entries);
}

export function formatEntryAsText(entry: StoredFormEntry): string {
  const date = new Date(entry.submittedAt).toLocaleString("en-US", {
    month: "long", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const lines = [
    `=== ${entry.formLabel} ===`,
    `Submitted: ${date}`,
    ``,
    ...entry.fields.map((f) => `${f.label}: ${f.value}`),
    ``,
  ];
  return lines.join("\n");
}
